// Copyright © 2026 Qcells. All rights reserved.
//
// This software is proprietary and confidential. Unauthorized use,
// duplication, or distribution of software is strictly prohibited.

/**
 * What an asset is *drawn as* on the single-line diagram.
 *
 * This replaces `config/knowledgeGraphGroups.ts` and is deliberately much
 * smaller than it. The old config carried 90 type→band→column rules because
 * type decided *position*. On the one-line, position is derived from the
 * `feeds` chain (see `utils/powerFlowRank.ts`), so type decides only three
 * things: whether the asset is electrically a point or a bar, which symbol it
 * will eventually be drawn with, and how heavy its conductors are.
 *
 * `role` is a claim about electrical topology, and it is not ours to invent:
 * WattSchema's SHACL profile states it per class — `UtilityMeterShape` declares
 * `feeds` and no `fedBy` (a source), `ITRackShape` is annotated *"Terminal load
 * — consumes power only"* (a leaf). The rows below transcribe that profile.
 * See docs/power-flow/power-flow-data-inventory.md §2.2.
 *
 * The profile is not vendored into this repo — it lives in
 * `shacl_profiles/example_profile/` in WattSchema — so these rows are a
 * transcription rather than a derivation, and a re-check against the Turtle is
 * the follow-up that would close that gap.
 *
 * `role` is used for **diagnostics, not placement**: a `PDUType` that lands at
 * rank 0 means an upstream `feeds` edge is missing from the payload, and the
 * model reports it (`suspectRoots`) rather than quietly drawing a PDU as if it
 * were a utility. Nothing here can move a node.
 */

import type { EntityTypeName } from '../types/generated/ontology.generated';
import { stripOntologyPrefix } from '../utils/entityHelpers';
import { isKnownEntityType } from '../utils/ontologyAccess';

/**
 * Electrical role, as the SHACL profile states it.
 *
 * - `source` — declares `feeds` and no `fedBy`. Legitimately a root.
 * - `bus`    — electrically a *bar*: everything tapped off it is at the same
 *              potential. Drawn as a horizontal line, not a chip.
 * - `series` — one in, one out (nominally). Transformers, UPS, PDUs.
 * - `load`   — terminal. Consumes only; legitimately a leaf.
 */
export type PowerRole = 'source' | 'bus' | 'series' | 'load';

/**
 * Which one-line symbol the asset gets. Every value renders as a chip in this
 * increment — the `symbols/` components are PR 4 — but the classification is
 * made here now so PR 4 is a rendering change and not a re-modelling one.
 */
export type PowerSymbol =
  | 'utility'
  | 'generator'
  | 'battery'
  | 'transformer'
  | 'switchgear'
  | 'busway'
  | 'transfer-switch'
  | 'ups'
  | 'pdu'
  | 'rack'
  | 'motor'
  | 'pump'
  | 'fan'
  | 'meter'
  | 'protection'
  | 'chip';

/**
 * Rotating machines on the load side. One family in the drawing — a circle
 * with a mark inside, filled when running — and one family to the live rules:
 * `annotateAsset` words them `Running` / `Idle`, `powerFlowLive` reads their
 * run status or draw, `powerFlowInference` counts them as the mechanical
 * branch. Anything that switched on `'motor'` switches on this.
 */
export const MOTOR_SYMBOLS: ReadonlySet<PowerSymbol> = new Set<PowerSymbol>([
  'motor',
  'pump',
  'fan',
]);

/** True for a motor, pump or fan. */
export const isMotorSymbol = (symbol: PowerSymbol): boolean => MOTOR_SYMBOLS.has(symbol);

/**
 * Symbols whose device is drawn **as the symbol**, on the conductor, with its
 * text beside it — the way a one-line draws a transformer or a motor. The
 * conductor lands on the symbol's centre (`PowerFlowNode.port`), not on the
 * middle of a box. Everything else — a bus, a load group, a symbol with no
 * glyph — keeps its box or bar.
 */
export const SYMBOL_NODE_SYMBOLS: ReadonlySet<PowerSymbol> = new Set<PowerSymbol>([
  'utility',
  'generator',
  'battery',
  'transformer',
  'ups',
  'pdu',
  'motor',
  'pump',
  'fan',
  'meter',
]);

/** True when a device with this symbol is drawn as the symbol itself. */
export const isSymbolNode = (symbol: PowerSymbol): boolean => SYMBOL_NODE_SYMBOLS.has(symbol);

/** Edge length of a symbol drawn as a node: the node height, so it fills the rank. */
export const SYMBOL_NODE_SIZE = 44;

/**
 * Conductor weight class. The one-line convention is that medium voltage is
 * drawn heavier than low voltage; `dc` covers the battery/UPS DC bus and
 * `unknown` is what an unmapped type gets.
 */
export type VoltageClass = 'mv' | 'lv' | 'dc' | 'unknown';

export interface PowerFlowSymbolRule {
  role: PowerRole;
  symbol: PowerSymbol;
  voltageClass: VoltageClass;
}

/**
 * Entity type → how it is drawn.
 *
 * Keyed on `EntityTypeName` rather than `string`, so a typo or a type the
 * ontology drops in a re-sync is a compile error rather than an asset that
 * silently falls through to the unmapped default.
 *
 * Only electrically meaningful types appear. An HVAC asset is here because it
 * is an electrical *load* on the mechanical switchgear — per design §11
 * assumption 4, the chilled-water loop is Floor Plan's job, so nothing here
 * describes cooling topology.
 */
export const POWER_FLOW_SYMBOLS: Partial<Record<EntityTypeName, PowerFlowSymbolRule>> = {
  // --- Sources -----------------------------------------------------------
  // `FeederType` is the utility intake in every payload we hold: it feeds the
  // main switchboards and nothing feeds it.
  PointOfInterconnection: { role: 'source', symbol: 'utility', voltageClass: 'mv' },
  UtilityMeterType: { role: 'source', symbol: 'utility', voltageClass: 'mv' },
  FeederType: { role: 'source', symbol: 'utility', voltageClass: 'mv' },
  // MOCK-GA10's intake: a power-quality meter on each board's utility main,
  // nothing upstream of it. `source` so the nine of them are not reported as
  // suspect roots; a PQ meter published mid-chain would surface as a role
  // violation instead, which is the right notice for a payload that did that.
  PQMeterType: { role: 'source', symbol: 'meter', voltageClass: 'lv' },
  GeneratorSetType: { role: 'source', symbol: 'generator', voltageClass: 'lv' },
  BESSType: { role: 'source', symbol: 'battery', voltageClass: 'dc' },

  // --- Buses -------------------------------------------------------------
  // `SwitchgearShape` puts `sh:minCount 1` on `busbarRating` as well as
  // `feeds`, which independently supports drawing these as a rated bar.
  SwitchgearType: { role: 'bus', symbol: 'switchgear', voltageClass: 'mv' },
  BuswayType: { role: 'bus', symbol: 'busway', voltageClass: 'lv' },
  PanelboardType: { role: 'bus', symbol: 'busway', voltageClass: 'lv' },
  MCCType: { role: 'bus', symbol: 'busway', voltageClass: 'lv' },
  RPPType: { role: 'bus', symbol: 'busway', voltageClass: 'lv' },

  // --- Series elements ---------------------------------------------------
  TransformerType: { role: 'series', symbol: 'transformer', voltageClass: 'mv' },
  ATSType: { role: 'series', symbol: 'transfer-switch', voltageClass: 'lv' },
  STSType: { role: 'series', symbol: 'transfer-switch', voltageClass: 'lv' },
  UPSType: { role: 'series', symbol: 'ups', voltageClass: 'lv' },
  PDUType: { role: 'series', symbol: 'pdu', voltageClass: 'lv' },
  RackPDUType: { role: 'series', symbol: 'pdu', voltageClass: 'lv' },
  MotorFeederType: { role: 'series', symbol: 'motor', voltageClass: 'lv' },
  SPDType: { role: 'series', symbol: 'protection', voltageClass: 'lv' },

  // --- Terminal loads ----------------------------------------------------
  // `ITRackShape`: "Terminal load — consumes power only".
  ITRackType: { role: 'load', symbol: 'rack', voltageClass: 'lv' },
  IT_Rack: { role: 'load', symbol: 'rack', voltageClass: 'lv' },

  // Mechanical plant, on the electrical side only: these are motors hanging off
  // the mechanical switchgear. Three glyphs, one family (`MOTOR_SYMBOLS`): a
  // pump and a fan get the P&ID marks an operator already reads, a chiller,
  // tower or condensing unit stays the plain `M` — no one-line standard draws a
  // chiller, and to the board it is a compressor motor.
  Chiller: { role: 'load', symbol: 'motor', voltageClass: 'lv' },
  Air_Cooled_Chiller: { role: 'load', symbol: 'motor', voltageClass: 'lv' },
  Cooling_Tower: { role: 'load', symbol: 'motor', voltageClass: 'lv' },
  Condensing_Unit: { role: 'load', symbol: 'motor', voltageClass: 'lv' },
  Chilled_Water_Pump: { role: 'load', symbol: 'pump', voltageClass: 'lv' },
  Condenser_Water_Pump: { role: 'load', symbol: 'pump', voltageClass: 'lv' },
  Cooling_Tower_Fan: { role: 'load', symbol: 'fan', voltageClass: 'lv' },
  CRAH: { role: 'load', symbol: 'fan', voltageClass: 'lv' },
  AHU: { role: 'load', symbol: 'fan', voltageClass: 'lv' },
  Air_Handling_Unit: { role: 'load', symbol: 'fan', voltageClass: 'lv' },
  Rooftop_Unit: { role: 'load', symbol: 'fan', voltageClass: 'lv' },
  Fan: { role: 'load', symbol: 'fan', voltageClass: 'lv' },
  Supply_Fan: { role: 'load', symbol: 'fan', voltageClass: 'lv' },
  Fan_Coil_Unit: { role: 'load', symbol: 'fan', voltageClass: 'lv' },
};

/**
 * Types that carry a *fluid or a space*, not electricity.
 *
 * Belt and braces since GA-11: the twin now publishes air and chilled water
 * with `feeds`/`isFedBy` and conductors with `suppliesPowerTo`, so the fluid
 * edges no longer reach the one-line at all (`flowRelationships.ts`). These
 * types stay off it even if one is ever published with a power predicate.
 *
 * The list is deliberately *only* the passive conveyors and the spaces. Every
 * pump, fan, chiller and cooling tower stays on the diagram, because each of
 * those genuinely is an electrical load on the mechanical switchboard — per
 * design §11 assumption 4 what belongs to Floor Plan is the *loop*, not the
 * motors hanging off the board. An `Evaporator` is here for the same reason a
 * pipe would be: it is a heat exchanger, not a motor.
 *
 * Membership is still derived, not enumerated: an entity type nobody has
 * classified yet keeps §7.1's behaviour and appears as a plain chip. This
 * removes only what is known to be the wrong kind of flow, and the count is
 * reported so the removal is visible.
 */
/**
 * Rooms, halls and zones. A space consumes power through what is in it, not as
 * an asset, and is the far end of an air path rather than part of one.
 */
export const SPACE_TYPES: ReadonlySet<string> = new Set<EntityTypeName>([
  'DataHall',
  'HVACZone',
  'Mechanical_Room',
  'ElectricalRoom',
  'BatteryRoom',
  'GeneratorRoom',
  'GeneratorYard',
  'Cold_Aisle',
  'Hot_Aisle',
]);

/** True when this type is a room, hall or zone. */
export const isSpaceType = (rawType: string): boolean =>
  SPACE_TYPES.has(stripOntologyPrefix(rawType));

export const NON_ELECTRICAL_TYPES: ReadonlySet<string> = new Set<EntityTypeName>([
  // Air and water conveyance — passive, and the other end of the `feeds` edge.
  'Air_Header',
  'Chiller_Plant_Header',
  'Chilled_Water_Loop',
  'Chilled_Water_Thermal_Energy_Storage_Tank',
  'Evaporator',
  ...(Array.from(SPACE_TYPES) as EntityTypeName[]),
]);

/** True when this type carries a fluid or is a space, rather than electricity. */
export const isNonElectricalType = (rawType: string): boolean => {
  const entityType = stripOntologyPrefix(rawType);
  return isKnownEntityType(entityType) && NON_ELECTRICAL_TYPES.has(entityType);
};

/**
 * Types that are a bus's *terminals*: drawn on the tap, never as a stage.
 *
 * MOCK-GA10 models each switchgear as 144 `CircuitBreakerType` nodes wired
 * `breaker → breaker` inside the board and `breaker → UPS / PDU / chiller` out
 * of it; the board itself has no flow edge, only `hasPart` to its breakers.
 * Left as vertices they would add three ranks the real site (GA-10) does not
 * have and leave every board off the diagram. `powerFlowGraph` folds each one
 * into the asset that `hasPart`s it and keeps the breaker on the conductor's
 * `via` list.
 *
 * A plain string set, not `EntityTypeName`: `CircuitBreakerType` is not in the
 * vendored ontology yet, which is also why `isAssetEntityType` rejects it.
 */
export const BUS_TERMINAL_TYPES: ReadonlySet<string> = new Set(['CircuitBreakerType']);

/** True when this type is a bus terminal, folded into its container. */
export const isBusTerminalType = (rawType: string): boolean =>
  BUS_TERMINAL_TYPES.has(stripOntologyPrefix(rawType));

/**
 * What an entity type not in the table above is drawn as.
 *
 * Unmapped is not the same as excluded: an asset with real `feeds` edges is on
 * the chain whatever its type, so it is drawn as a plain chip and *counted*
 * (`unmappedTypes` in the model's diagnostics). The predecessor config made the
 * same choice for unconfigured types and it is the right one — an entity type
 * the ontology gains between re-syncs should appear on the diagram looking
 * plain, not vanish from it.
 *
 * `series` is the neutral role: it claims neither that the asset is a
 * legitimate root nor that it is a legitimate leaf, so an unmapped type never
 * suppresses a real diagnostic and never raises a false one.
 */
export const UNMAPPED_SYMBOL: PowerFlowSymbolRule = {
  role: 'series',
  symbol: 'chip',
  voltageClass: 'unknown',
};

/**
 * How *this* entity type is drawn. Never throws; unmapped types are counted.
 * A `brick:`-prefixed spelling draws as its bare equivalent.
 */
export const symbolRuleFor = (rawType: string): PowerFlowSymbolRule => {
  const entityType = stripOntologyPrefix(rawType);
  return (
    (isKnownEntityType(entityType) ? POWER_FLOW_SYMBOLS[entityType] : undefined) ?? UNMAPPED_SYMBOL
  );
};

/** True when the ontology tells us how to draw this type. */
export const hasSymbolRule = (rawType: string): boolean => {
  const entityType = stripOntologyPrefix(rawType);
  return isKnownEntityType(entityType) && POWER_FLOW_SYMBOLS[entityType] !== undefined;
};
